import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AbsenceItemComponent } from '@app/+authenticated/+schedule/employee/schedule-employee-n-period/schedule-items/absence/absence-item.component';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { TimePipe } from '@app/pipes/time.pipe';
import { getAccountSubscription } from '@app/reducers/account/account-service.selectors';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { AvailabilityType } from '@app/reducers/orm/availability/availability.model';
import { Store } from '@ngrx/store';
import { _, TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconCalendar2Regular } from '@sb/mgc-icons/mgcIcon-calendar_2_regular.icon';
import { mgcIconCalendarAddRegular } from '@sb/mgc-icons/mgcIcon-calendar_add_regular.icon';
import { mgcIconChat1Regular } from '@sb/mgc-icons/mgcIcon-chat_1_regular.icon';
import { mgcIconCheckRegular } from '@sb/mgc-icons/mgcIcon-check_regular.icon';
import { mgcIconForbidCircleRegular } from '@sb/mgc-icons/mgcIcon-forbid_circle_regular.icon';
import { mgcIconTask2Regular } from '@sb/mgc-icons/mgcIcon-task_2_regular.icon';
import { mgcIconTransfer3Regular } from '@sb/mgc-icons/mgcIcon-transfer_3_regular.icon';
import { ScheduleItemActionsComponent } from '@sb/schedule/primitives';
import { hasPermission } from '@sb/shared/permission/util';
import { UserColorBgPipe } from '@sb/shared/theme';
import { TooltipDirective } from '@sb/tooltip';
import { ButtonComponent, IconButtonComponent } from '@sb/ui/button';
import { SbDatePickerComponent } from '@sb/ui/date-picker';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { MenuComponent, MenuItemComponent, MenuTriggerDirective } from '@sb/ui/menu';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import {
  hasAtLeastSubscriptionPlan,
  parseDate,
  toISODateString,
  type PlainDate,
} from '@shiftbase-com/ts-shared/utilities';
import { switchMap } from 'rxjs/operators';

import { ScheduleModel } from '../../../reducers/orm/schedule/schedule.model';
import { lazySelect } from '../../../shared/lazy-select.observable';
import { getMyScheduleData } from './personal-schedule.helper';
import type { MyScheduleWeek } from './personal-schedule.interfaces';

const DEFAULT_ITEM_COLOR = '#2399e4';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'personal-schedule',
  templateUrl: './personal-schedule.component.html',
  imports: [
    LoadingStateComponent,
    LocaleDatePipe,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    ScheduleItemActionsComponent,
    TranslatePipe,
    SbDatePickerComponent,
    MenuTriggerDirective,
    MenuComponent,
    MenuItemComponent,
    TimePipe,
    NgTemplateOutlet,
    AbsenceItemComponent,
    TooltipDirective,
    UserColorBgPipe,
  ],
})
export class PersonalScheduleComponent {
  #store = inject<Store<AppState>>(Store);
  #router = inject(Router);

  employeeId = input.required<string>();
  date = input(new Date());

  changeDateEmitter = output<string>();

  AvailabilityType = AvailabilityType;
  defaultItemColor = DEFAULT_ITEM_COLOR;

  // Marked with `_` so the extractor still sees the literals now that the header row loops.
  dayLabels: string[] = [_('Mo'), _('Tu'), _('We'), _('Th'), _('Fr'), _('Sa'), _('So')];

  // Paging is applied locally before the parent routes the new date back in, so the calendar moves
  // on the click rather than after the round trip.
  selectedDate = linkedSignal(() => this.date());

  plainDate = computed(() => toISODateString(this.selectedDate()));

  data = toSignal(
    toObservable(computed(() => ({ employeeId: this.employeeId(), date: this.selectedDate() }))).pipe(
      switchMap(({ employeeId, date }) => this.#store.pipe(lazySelect(getMyScheduleData(employeeId, date)))),
    ),
  );

  firstEditableWeek = computed(() => this.data()?.firstEditableWeek);

  #accountSubscription = this.#store.selectSignal(getAccountSubscription);
  #permissionState = this.#store.selectSignal(getPermissionState);

  hasAtleastBasic = computed(() => {
    const subscription = this.#accountSubscription();

    return subscription ? hasAtLeastSubscriptionPlan(PlanType.BASIC, subscription) : false;
  });

  canAddShifts = computed(() =>
    hasPermission(
      { permissions: ['Create roster', 'Create own roster'], userId: this.employeeId(), departments: 'any' },
      this.#permissionState(),
    ),
  );

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconCalendarAddRegular,
      mgcIconCalendar2Regular,
      mgcIconChat1Regular,
      mgcIconTask2Regular,
      mgcIconTransfer3Regular,
      mgcIconForbidCircleRegular,
      mgcIconCheckRegular,
    ]);
  }

  changeDate(date: PlainDate) {
    this.selectedDate.set(parseDate(date));
    this.changeDateEmitter.emit(toISODateString(this.selectedDate()));
  }

  onEditAvailability(week: MyScheduleWeek) {
    if (!week.canEditAvailability) {
      return;
    }

    const start = toISODateString(week.firstEditableAvailabilityDate.date);
    const end = toISODateString(week.lastDayOfWeek.date);
    const params = { start, end, employee: this.employeeId() };

    this.#router.navigate(['', { outlets: { modal: ['availability', params] } }]);
  }

  onEditShift(shift: ScheduleModel) {
    this.#router.navigate([
      '',
      { outlets: { modal: ['schedule-shift', shift.occurrence_id, 'edit', { showEmployee: false }] } },
    ]);
  }

  onDeleteShift(shift: ScheduleModel) {
    this.#router.navigate(['', { outlets: { modal: ['schedule-shift', shift.occurrence_id, 'delete'] } }]);
  }

  onViewShift(shift: ScheduleModel) {
    this.#router.navigate(['', { outlets: { modal: ['schedule-shift', shift.occurrence_id] } }]);
  }

  onAddShift() {
    this.#router.navigate([
      '',
      { outlets: { modal: ['schedule-shift', { employee: this.employeeId(), showEmployee: false }] } },
    ]);
  }

  onCalendarSync() {
    this.#router.navigate(['', { outlets: { modal: ['calendar-sync'] } }]);
  }
}
